// Such-Seite (2.B): Filter-Syntax mit Chips, Facetten, Reiter Alles/Mails/Anhänge/Einrichtungen, Modus „Bob fragen“.
// Suchen in Tabs (D-071): Jede Suche ist ein Tab (lib/suche.ts), Treffer bleiben stehen, ein Klick öffnet den
// Thread rechts daneben (/search/t/:id), j/k gehen durch die Treffer.
import { useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import {
  FILTER_LABELS,
  TEAM,
  isUserKey,
  withFilter,
  type SearchFilters,
  type ThreadListItem,
} from '@mail/shared';
import { api } from '../lib/api.ts';
import { bytes, fmtWhen, participantsText } from '../lib/format.ts';
import { useStore } from '../lib/store.ts';
import {
  aendereTab,
  getSuche,
  neuerTab,
  schliesseTab,
  tabFuer,
  useSuche,
  waehleTab,
  type SuchReiter,
  type SuchTab,
} from '../lib/suche.ts';
import { BobAntwort } from '../components/BobAntwort.tsx';
import { Conversation } from '../components/Conversation.tsx';
import { CrmPanel } from '../components/CrmPanel.tsx';

const BEISPIELE = [
  'von:meier',
  'schule:Lindenpark ist:offen',
  'hat:pdf nach:2026-09-01',
  '"Liederliste 3b"',
  'zugewiesen:lars -newsletter',
];
const STATUS_LABEL: Record<string, string> = {
  offen: 'offen',
  wartet: 'wartet auf Antwort',
  spaeter: 'später',
  erledigt: 'erledigt',
  info: 'Info',
  junk: 'Junk',
};
const REITER: Array<[SuchReiter, string]> = [
  ['alles', 'Alles'],
  ['mails', 'Mails'],
  ['anhaenge', 'Anhänge'],
  ['einrichtungen', 'Einrichtungen'],
];

/** Suche eines Tabs ausführen – läuft auch weiter, wenn inzwischen ein anderer Tab aktiv ist. */
function suchen(tabId: number, term: string) {
  const t = term.trim();
  if (!t) return aendereTab(tabId, { q: '', eingabe: '', res: null, laden: false });
  aendereTab(tabId, { q: t, eingabe: t, laden: true });
  void api
    .search(t)
    .then((res) => aendereTab(tabId, { res }))
    .catch(() => aendereTab(tabId, { res: null }))
    .finally(() => aendereTab(tabId, { laden: false }));
}

export function SearchPage() {
  const nav = useNavigate();
  const { id: idParam } = useParams();
  const threadId = idParam ? Number(idParam) : null;
  const [params, setParams] = useSearchParams();
  const mailboxes = useStore((s) => s.mailboxes);
  const crmOpen = useStore((s) => s.crmOpen);
  const own = useMemo(() => new Set(mailboxes.map((m) => m.address.toLowerCase())), [mailboxes]);
  const { tabs, aktiv } = useSuche();
  const tab: SuchTab = tabs.find((t) => t.id === aktiv) ?? tabs[0]!;
  const { q, modus, res, laden, bob } = tab;
  const patch = (p: Partial<SuchTab>) => aendereTab(tab.id, p);

  const fragen = (tabId: number, frage: string) => {
    const f = frage.trim();
    if (!f) return;
    aendereTab(tabId, { q: f, eingabe: f, bob: { laden: true, frage: f } });
    void api
      .bobFrage(f)
      .then((r) => {
        aendereTab(tabId, {
          bob: {
            laden: false,
            frage: f,
            antwort: r.antwort,
            schritte: r.schritte,
            kosten: r.kostenEur,
            ...(r.error ? { error: r.error } : {}),
          },
        });
        if (r.draftId && r.threadId) {
          aendereTab(tabId, { thread: r.threadId });
          void nav(`/search/t/${r.threadId}?draft=${r.draftId}`);
        }
        const s = r.schritte.find((x) => x.tool === 'mail_suchen');
        const sq =
          s && typeof s.input === 'object' && s.input && 'query' in s.input
            ? String(s.input.query)
            : '';
        if (sq) suchen(tabId, sq);
      })
      .catch((e: Error) =>
        aendereTab(tabId, { bob: { laden: false, frage: f, error: e.message } }),
      );
  };
  const go = () => (modus === 'bob' ? fragen(tab.id, tab.eingabe) : suchen(tab.id, tab.eingabe));

  // Von außen mit ?q= aufgerufen (Bob, Links): passenden Tab wählen oder anlegen, Adresse danach sauber
  useEffect(() => {
    const t = params.get('q');
    if (t) {
      const z = tabFuer(t, params.get('bob') ? 'bob' : 'suchen');
      setParams({}, { replace: true });
      if (z.modus === 'suchen') suchen(z.id, t);
      else fragen(z.id, t);
    }
  }, []);
  // Tabs mit Frage, aber ohne Treffer (nach Neuladen): Suchen nachholen, damit die Zähler stimmen. Bob-Fragen
  // nicht – die kosten Geld und laufen erst wieder auf Klick.
  useEffect(() => {
    for (const t of getSuche().tabs)
      if (t.modus === 'suchen' && t.q && !t.res && !t.laden) suchen(t.id, t.q);
  }, [tab.id, tab.q]);
  // Geöffneter Thread gehört zum Tab (Tab-Wechsel bringt ihn wieder)
  useEffect(() => {
    if (tab.thread !== threadId) patch({ thread: threadId });
  }, [threadId]);
  // Suchfeld nur fokussieren, wenn noch nichts gesucht ist (sonst nimmt es dem Antwortfeld den Fokus)
  useEffect(() => {
    if (!tab.q && !threadId) document.getElementById('sq')?.focus();
  }, [tab.id, tab.q, threadId]);
  // j/k durch die Treffer (Shell überlässt das der Suchseite), Enter öffnet den markierten
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) ||
        (e.target as HTMLElement)?.isContentEditable
      )
        return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key !== 'j' && e.key !== 'k') return;
      const l = getSuche().tabs.find((t) => t.id === getSuche().aktiv)?.res?.threads ?? [];
      if (!l.length) return;
      const i = l.findIndex((t) => t.id === threadId);
      const n = e.key === 'j' ? l[Math.min(i + 1, l.length - 1)] : l[Math.max(i - 1, 0)];
      if (n) void nav(`/search/t/${n.id}`);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [threadId, nav]);

  const wechsle = (id: number) => {
    waehleTab(id);
    const z = getSuche().tabs.find((t) => t.id === id);
    void nav(z?.thread ? `/search/t/${z.thread}` : '/search');
  };
  const schliesse = (id: number) => {
    const z = schliesseTab(id);
    if (id === aktiv) void nav(z.thread ? `/search/t/${z.thread}` : '/search');
  };
  const neu = () => {
    neuerTab();
    void nav('/search');
  };

  const chips = res
    ? (Object.keys(res.parsed.filters) as Array<keyof SearchFilters>).filter(
        (k) => res.parsed.filters[k],
      )
    : [];
  const setFilter = (k: keyof SearchFilters, v: string | null) =>
    suchen(tab.id, withFilter(q, k, v));
  const mb = (id: number) => mailboxes.find((m) => m.id === id);
  const anz = {
    alles: 0,
    mails: res?.threads.length ?? 0,
    anhaenge: res?.attachments.length ?? 0,
    einrichtungen: res?.institutions.length ?? 0,
  };

  const Treffer = ({ t }: { t: ThreadListItem }) => (
    <button
      className={`s-hit${t.id === threadId ? ' on' : ''}`}
      onClick={() => void nav(`/search/t/${t.id}`)}
    >
      <div className="s-h1">
        {mb(t.mailboxId) && (
          <span className={`dot c-${mb(t.mailboxId)!.color}`} title={mb(t.mailboxId)!.address} />
        )}
        <span>{participantsText(t.participants, own)}</span>
        {t.crmInstitutionName && <span className="pill einrichtung">{t.crmInstitutionName}</span>}
        {t.crmRole && <span className="pill mut">{t.crmRole}</span>}
        {t.status === 'done' && <span className="pill mut">erledigt</span>}
        {t.status === 'later' && <span className="pill warn">später</span>}
        {t.category !== 'wichtig' && <span className="pill mut">{t.category}</span>}
        {t.assignedTo && isUserKey(t.assignedTo) && (
          <span className="pill mut">bei {TEAM[t.assignedTo].name}</span>
        )}
        {t.messageCount > 1 && <span className="n">{t.messageCount}</span>}
        {t.hasAttachments && <span title="Anhang">📎</span>}
        <span className="s-d">{fmtWhen(t.lastDate)}</span>
      </div>
      <div className="s-subj">{t.subject || '(kein Betreff)'}</div>
      <div
        className="s-snip"
        {...(t.headline
          ? { dangerouslySetInnerHTML: { __html: t.headline } }
          : { children: t.snippet })}
      />
    </button>
  );

  return (
    <>
      <section className="page-body" aria-label="Suche">
        <div className="s-wrap">
          <div className="s-tabbar" role="tablist" aria-label="Suchen">
            {tabs.map((t) => (
              <div
                key={t.id}
                role="tab"
                aria-selected={t.id === aktiv}
                tabIndex={0}
                className={`s-tab${t.id === aktiv ? ' on' : ''}${t.modus === 'bob' ? ' bob' : ''}`}
                onClick={() => wechsle(t.id)}
                onKeyDown={(e) => e.key === 'Enter' && wechsle(t.id)}
                onAuxClick={(e) => e.button === 1 && schliesse(t.id)}
                title={t.q || 'Neue Suche'}
              >
                <span className="s-tab-l">
                  {t.modus === 'bob' ? '✦ ' : ''}
                  {t.q || 'Neue Suche'}
                </span>
                {t.laden ? (
                  <span className="spin" />
                ) : t.res ? (
                  <span className="s-tab-n">{t.res.threads.length}</span>
                ) : null}
                <button
                  className="s-tab-x"
                  onClick={(e) => {
                    e.stopPropagation();
                    schliesse(t.id);
                  }}
                  title="Suche schließen"
                  aria-label="Suche schließen"
                >
                  ×
                </button>
              </div>
            ))}
            <button className="s-tab-plus" onClick={neu} title="Neue Suche in eigenem Tab">
              +
            </button>
          </div>
          <div className="s-mode">
            <button
              className={modus === 'suchen' ? 'on' : ''}
              onClick={() => patch({ modus: 'suchen' })}
            >
              Suchen
            </button>
            <button
              className={`bobm ${modus === 'bob' ? 'on' : ''}`}
              onClick={() => patch({ modus: 'bob' })}
            >
              ✦ Bob fragen
            </button>
          </div>
          <div className={`s-box ${modus === 'bob' ? 'bob' : ''}`}>
            <span aria-hidden="true">{modus === 'bob' ? '✦' : '⌕'}</span>
            <input
              id="sq"
              placeholder={
                modus === 'bob'
                  ? 'Bob fragen: „Welche Schulsong-Anfragen sind seit Montag offen?“ oder „Schreib Susanne eine Antwort …“'
                  : 'Suchen in allen Postfächern: Name, Schule, Betreff, Text … oder von: schule: ist: hat: nach:'
              }
              value={tab.eingabe}
              onChange={(e) => patch({ eingabe: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') go();
                if (e.key === 'Escape') void nav('/');
              }}
            />
            <button className="btn pri" onClick={go}>
              {modus === 'bob' ? 'Fragen' : 'Suchen'}
            </button>
          </div>
          {modus === 'suchen' && (
            <div className="s-ops">
              {chips.length > 0 ? (
                chips.map((k) => (
                  <button
                    key={k}
                    className="chip"
                    onClick={() => setFilter(k, null)}
                    title="Filter entfernen"
                  >
                    {FILTER_LABELS[k]}: {res!.parsed.filters[k]} ×
                  </button>
                ))
              ) : (
                <>
                  <span>Beispiele:</span>
                  {BEISPIELE.map((b) => (
                    <code key={b} onClick={() => suchen(tab.id, b)} style={{ cursor: 'pointer' }}>
                      {b}
                    </code>
                  ))}
                  <span>
                    · auch Erledigtes; Junk nur mit <code>ist:junk</code>
                  </span>
                </>
              )}
            </div>
          )}
          {modus === 'bob' && bob && (
            <div className="s-ans">
              <div className="s-ans-h">
                <b>✦ Bob</b>
                <span className="s-q">{bob.frage}</span>
                {bob.laden && <span className="spin" />}
              </div>
              {bob.schritte && bob.schritte.length > 0 && (
                <div className="s-src">
                  Bob hat gesucht mit:{' '}
                  {bob.schritte.map((s, i) => (
                    <code key={i}>
                      {s.tool}{' '}
                      {typeof s.input === 'object' && s.input
                        ? JSON.stringify(s.input).slice(0, 60)
                        : ''}
                    </code>
                  ))}
                </div>
              )}
              {bob.error && <p className="err">{bob.error}</p>}
              {bob.antwort && <BobAntwort text={bob.antwort} />}
              {bob.kosten ? (
                <span className="muted" style={{ fontSize: 12 }}>
                  {bob.kosten.toFixed(3)} €
                </span>
              ) : null}
            </div>
          )}
          <div className="s-body">
            {res && res.threads.length > 0 && (
              <aside className="s-side">
                <Facette
                  titel="Status"
                  items={Object.entries(res.facets.status).map(([k, n]) => ({
                    k,
                    label: STATUS_LABEL[k] ?? k,
                    n,
                  }))}
                  aktiv={res.parsed.filters.ist}
                  onPick={(k) => setFilter('ist', k === 'spaeter' ? 'später' : k)}
                />
                <Facette
                  titel="Postfach"
                  items={Object.entries(res.facets.mailbox).map(([k, n]) => ({
                    k: mb(Number(k))?.address ?? k,
                    label: mb(Number(k))?.address ?? k,
                    n,
                    color: mb(Number(k))?.color,
                  }))}
                  aktiv={res.parsed.filters.postfach}
                  onPick={(k) => setFilter('postfach', k)}
                />
                {res.facets.institution.length > 0 && (
                  <Facette
                    titel="Einrichtung"
                    items={res.facets.institution.map((i) => ({
                      k: i.name,
                      label: i.name,
                      n: i.n,
                    }))}
                    aktiv={res.parsed.filters.schule}
                    onPick={(k) => setFilter('schule', k)}
                  />
                )}
                {Object.keys(res.facets.role).length > 0 && (
                  <Facette
                    titel="Rolle"
                    items={Object.entries(res.facets.role).map(([k, n]) => ({ k, label: k, n }))}
                    aktiv={res.parsed.filters.rolle}
                    onPick={(k) => setFilter('rolle', k)}
                  />
                )}
                {Object.keys(res.facets.assigned).length > 0 && (
                  <Facette
                    titel="Person"
                    items={Object.entries(res.facets.assigned).map(([k, n]) => ({
                      k,
                      label: isUserKey(k) ? TEAM[k].name : k,
                      n,
                    }))}
                    aktiv={res.parsed.filters.zugewiesen}
                    onPick={(k) => setFilter('zugewiesen', k)}
                  />
                )}
                <Facette
                  titel="Zeitraum"
                  items={Object.entries(res.facets.month)
                    .sort((a, b) => b[0].localeCompare(a[0]))
                    .slice(0, 8)
                    .map(([k, n]) => ({
                      k,
                      label: new Date(k + '-01').toLocaleDateString('de-DE', {
                        month: 'long',
                        year: 'numeric',
                      }),
                      n,
                    }))}
                  aktiv={res.parsed.filters.nach?.slice(0, 7)}
                  onPick={(k) =>
                    suchen(tab.id, withFilter(withFilter(q, 'nach', k + '-01'), 'vor', k + '-31'))
                  }
                />
              </aside>
            )}
            <div className="s-res">
              {res && (
                <div className="s-tabs">
                  {REITER.map(([t, label]) => (
                    <button
                      key={t}
                      className={tab.reiter === t ? 'on' : ''}
                      onClick={() => patch({ reiter: t })}
                    >
                      {label}
                      <span>{t === 'alles' ? '' : ` ${anz[t]}`}</span>
                    </button>
                  ))}
                </div>
              )}
              {laden && <p className="empty">Sucht …</p>}
              {res && !laden && !res.threads.length && (
                <div className="s-empty">
                  <b>Nichts gefunden für „{res.q}“.</b>
                  <span>
                    Probier einen anderen Begriff, den Namen der Schule oder Filter wie{' '}
                    <code>von:</code>, <code>schule:</code>, <code>ist:erledigt</code>.
                  </span>
                </div>
              )}
              {(tab.reiter === 'alles' || tab.reiter === 'einrichtungen') &&
                res &&
                res.institutions.length > 0 && (
                  <div className="s-sch">
                    {res.institutions.map((i) => (
                      <div key={i.id} className="s-schc">
                        <b>{i.name}</b>
                        <span>
                          {i.n} {i.n === 1 ? 'Gespräch' : 'Gespräche'} ·{' '}
                          <a href={i.crmUrl} target="_blank" rel="noopener">
                            im CRM ↗
                          </a>{' '}
                          ·{' '}
                          <button className="linkbtn" onClick={() => setFilter('schule', i.name)}>
                            nur diese
                          </button>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              {(tab.reiter === 'alles' || tab.reiter === 'anhaenge') &&
                res &&
                res.attachments.length > 0 && (
                  <div className="s-att">
                    {res.attachments.map((a) => (
                      <a
                        key={a.id}
                        className="s-attc"
                        href={`/api/attachments/${a.id}`}
                        target="_blank"
                        rel="noopener"
                      >
                        <span className="s-pdf">
                          {a.mime.includes('pdf')
                            ? 'PDF'
                            : a.mime.split('/')[1]?.slice(0, 4).toUpperCase()}
                        </span>
                        <span>
                          {a.filename}
                          <small>
                            {bytes(a.size)} · {a.subject} · {fmtWhen(a.date)}
                          </small>
                        </span>
                      </a>
                    ))}
                  </div>
                )}
              {(tab.reiter === 'alles' || tab.reiter === 'mails') &&
                res?.threads.map((t) => <Treffer key={t.id} t={t} />)}
            </div>
          </div>
        </div>
      </section>
      {threadId !== null && (
        <>
          <Conversation id={threadId} />
          {crmOpen && <CrmPanel threadId={threadId} />}
        </>
      )}
    </>
  );
}

function Facette({
  titel,
  items,
  aktiv,
  onPick,
}: {
  titel: string;
  items: Array<{ k: string; label: string; n: number; color?: string | undefined }>;
  aktiv?: string | undefined;
  onPick: (k: string) => void;
}) {
  if (!items.length) return null;
  return (
    <div className="sf">
      <div className="blk-h">{titel}</div>
      {items.map((i) => (
        <button
          key={i.k}
          className={`sf-b ${aktiv && i.k.toLowerCase().includes(aktiv.toLowerCase()) ? 'on' : ''}`}
          onClick={() => onPick(i.k)}
        >
          {i.color && <span className={`dot c-${i.color}`} />}
          <span>{i.label}</span>
          <span>{i.n}</span>
        </button>
      ))}
    </div>
  );
}
